import ClearIcon from '@mui/icons-material/Clear';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Popover,
  Stack,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  getAdvancedPopoverFooterAreaSx,
  getAdvancedPopoverHeaderAreaSx,
  getAdvSearchPopoverPaperSx,
  getAdvancedPopoverRootSx,
  getAdvancedPopoverTitleSx,
  getAdvancedPopoverInputsAreaSx,
  getAdvSearchThreshold,
  advSearchAnchorOrigin,
  advSearchTransformOrigin,
  advSearchElevation,
} from 'components/Search/styles';
import { useAdvancedSearchContext } from './useAdvancedSearchContext';

type SearchAdvancedPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onCloseCallback: () => void;
  popoverSx?: SxProps<Theme>;
  advancedPopoverFieldsComponent?: React.ReactNode;
};

/**
 * Advanced Search Popover Component
 * Basic container for the advanced search popover fields.
 * - advancedPopoverFieldsComponent: Component to render the advanced search fields
 * eg: <EditorialPopoverFields />
 */
export const SearchAdvancedPopover = memo(
  ({
    open,
    anchorEl,
    onCloseCallback,
    popoverSx,
    advancedPopoverFieldsComponent = null,
  }: SearchAdvancedPopoverProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { setStagedFilters, applyFilters } = useAdvancedSearchContext();

    // Translation strings
    const strClearAll = t('blox.actions.clear_all');
    const strApply = t('blox.actions.apply');
    const strAdvancedFilters = t('blox.navigation.assets.advanced_filters');
    const strCloseWithoutApplying = t('blox.general.close_without_applying');

    const handleApplyFilters = () => {
      applyFilters(); // apply the staged filters
      localOnClose(); // close the popover
    };

    const localOnClose = (
      event?: React.SyntheticEvent | any,
      reason?: 'backdropClick' | 'escapeKeyDown',
    ) => {
      onCloseCallback(); // close the popover via parent callback
    };

    const clearAllStagedFilters = () => {
      setStagedFilters((prev) => {
        const clearedFilters = { ...prev };
        Object.keys(clearedFilters).forEach((key) => {
          clearedFilters[key] = undefined;
        });
        return clearedFilters;
      });
    };

    // if no advanced fields component, no point in rendering
    if (!advancedPopoverFieldsComponent) {
      return null;
    }

    return (
      <Popover
        data-testid="search-advanced-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={(event, reason) => localOnClose(event, reason)}
        elevation={advSearchElevation}
        marginThreshold={getAdvSearchThreshold(theme)}
        anchorOrigin={advSearchAnchorOrigin}
        transformOrigin={advSearchTransformOrigin}
        slotProps={{
          root: {
            sx: {
              ...getAdvancedPopoverRootSx(theme),
            },
          },
          paper: {
            sx: {
              ...getAdvSearchPopoverPaperSx(theme),
              ...(typeof popoverSx === 'function' ? popoverSx(theme) : popoverSx),
            },
          },
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            gridArea: 'advancedheader',
            ...getAdvancedPopoverHeaderAreaSx(theme),
          }}
        >
          <Typography sx={getAdvancedPopoverTitleSx}>{strAdvancedFilters}</Typography>
          <Tooltip
            title={strCloseWithoutApplying}
            disableInteractive
          >
            <IconButton
              onClick={localOnClose}
              size="small"
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack
          data-testid="search-advanced-popover-stack"
          direction="column"
          sx={[
            {
              justifyContent: 'space-between',
            },
            ...(Array.isArray(getAdvancedPopoverInputsAreaSx)
              ? getAdvancedPopoverInputsAreaSx
              : [getAdvancedPopoverInputsAreaSx]),
          ]}
        >
          {/* Advanced Search Fields Body */}
          {advancedPopoverFieldsComponent}
        </Stack>

        <Stack
          direction="row"
          sx={[
            {
              justifyContent: 'flex-end',
              alignItems: 'center',
            },
            ...(Array.isArray(getAdvancedPopoverFooterAreaSx)
              ? getAdvancedPopoverFooterAreaSx
              : [getAdvancedPopoverFooterAreaSx]),
          ]}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={clearAllStagedFilters}
            color="info"
            disableElevation
            sx={{ width: '100px' }}
            data-testid="advanced-filters-clear-all"
          >
            {strClearAll}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleApplyFilters}
            disableElevation
            sx={{ width: '100px' }}
            data-testid="advanced-filters-apply"
          >
            {strApply}
          </Button>
        </Stack>
      </Popover>
    );
  },
);
SearchAdvancedPopover.displayName = 'SearchAdvancedPopover';
